import {
  CANDIDATE_FIELD_LABELS,
  LAYOUT_STANDARD_FIELDS,
  type LayoutEntity,
  type OverviewLayout,
} from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type FieldDefinitionView } from '../../lib/client.ts';
import { layoutQuery } from '../../lib/custom-fields.ts';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';

const DESCRIPTION: Record<LayoutEntity, string> = {
  candidates: 'How the candidate page groups its fields.',
  companies: 'How a company groups its custom fields.',
  contacts: 'How a contact groups its custom fields.',
  projects: "How the project page's Custom fields card groups them.",
  deals: 'How a deal groups its custom fields.',
  jobs: 'How a job groups its custom fields.',
};

/**
 * Settings → Fields → Overview layout (RELAY-944; per kind since TS-1752): the groups a
 * record's page shows and which field goes where. Edits a draft and saves the whole layout
 * with one PUT; a field not placed anywhere is still shown by the page, in a trailing group,
 * so removing a field here never hides data. Companies and projects place custom fields only.
 * Remount it per kind (`key={entity}`): the draft belongs to one kind's layout.
 */
export function LayoutEditor({
  entity,
  definitions,
  canManage,
}: {
  entity: LayoutEntity;
  definitions: readonly FieldDefinitionView[];
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const stored = useQuery({
    queryKey: layoutQuery(entity),
    queryFn: () => api.layouts.get(entity),
  });
  const [draft, setDraft] = useState<OverviewLayout | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (stored.data && draft === null) setDraft(stored.data.layout);
  }, [stored.data, draft]);
  const save = useMutation({
    mutationFn: (layout: OverviewLayout) => api.layouts.put({ entity, layout }),
    onSuccess: async (res) => {
      setError(null);
      setDraft(res.layout);
      await qc.invalidateQueries({ queryKey: layoutQuery(entity) });
    },
    onError: (err) => setError(describeError(err)),
  });
  if (!draft) return null;

  const labelOf = (f: string) =>
    f.startsWith('custom.')
      ? (definitions.find((d) => `custom.${d.key}` === f)?.label ?? f)
      : (CANDIDATE_FIELD_LABELS[f] ?? f);
  const placed = new Set(draft.groups.flatMap((g) => g.fields));
  const unplaced = [
    ...LAYOUT_STANDARD_FIELDS[entity],
    ...definitions.map((d) => `custom.${d.key}`),
  ].filter((f) => !placed.has(f));
  const update = (groups: OverviewLayout['groups']) => setDraft({ groups });
  const moveGroup = (i: number, delta: -1 | 1) => {
    const j = i + delta;
    if (j < 0 || j >= draft.groups.length) return;
    const next = [...draft.groups];
    [next[i], next[j]] = [next[j]!, next[i]!];
    update(next);
  };
  const dirty = JSON.stringify(draft) !== JSON.stringify(stored.data?.layout);

  return (
    <section className="mt-8" data-testid="layout-editor">
      <div className="mb-2 flex items-baseline justify-between">
        <div>
          <h3 className="text-lg font-semibold">Overview layout</h3>
          <p className="text-muted-foreground text-sm">
            {DESCRIPTION[entity]} A field left out still shows, in a trailing group.
          </p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                update([...draft.groups, { name: `Group ${draft.groups.length + 1}`, fields: [] }])
              }
              data-testid="layout-add-group"
            >
              <Plus /> Group
            </Button>
            <Button
              size="sm"
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate(draft)}
              data-testid="layout-save"
            >
              Save layout
            </Button>
          </div>
        )}
      </div>
      {error && (
        <p className="text-danger mb-2 text-sm" role="alert">
          {error}
        </p>
      )}
      <div className="flex flex-col gap-3">
        {draft.groups.map((g, i) => (
          <div key={i} className="border-border rounded-md border p-3" data-testid="layout-group">
            <div className="mb-2 flex items-center gap-2">
              <Input
                aria-label={`Group ${i + 1} name`}
                value={g.name}
                disabled={!canManage}
                onChange={(e) =>
                  update(draft.groups.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))
                }
                className="max-w-xs"
              />
              {canManage && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Move group ${g.name} up`}
                    disabled={i === 0}
                    onClick={() => moveGroup(i, -1)}
                  >
                    <ArrowUp />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Move group ${g.name} down`}
                    disabled={i === draft.groups.length - 1}
                    onClick={() => moveGroup(i, 1)}
                  >
                    <ArrowDown />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove group ${g.name}`}
                    onClick={() => update(draft.groups.filter((_, j) => j !== i))}
                  >
                    <X />
                  </Button>
                </>
              )}
            </div>
            <ul className="flex flex-wrap gap-2">
              {g.fields.map((f) => (
                <li
                  key={f}
                  className="bg-surface inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm"
                  data-testid={`placed-${f}`}
                >
                  {labelOf(f)}
                  {canManage && (
                    <button
                      type="button"
                      aria-label={`Remove ${labelOf(f)} from ${g.name}`}
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() =>
                        update(
                          draft.groups.map((x, j) =>
                            j === i ? { ...x, fields: x.fields.filter((y) => y !== f) } : x,
                          ),
                        )
                      }
                    >
                      <X className="size-3" />
                    </button>
                  )}
                </li>
              ))}
              {canManage && unplaced.length > 0 && (
                <li>
                  <Select
                    value=""
                    onValueChange={(f) =>
                      update(
                        draft.groups.map((x, j) =>
                          j === i ? { ...x, fields: [...x.fields, f] } : x,
                        ),
                      )
                    }
                  >
                    <SelectTrigger className="h-8 w-44" aria-label={`Add field to ${g.name}`}>
                      <SelectValue placeholder="Add field…" />
                    </SelectTrigger>
                    <SelectContent>
                      {unplaced.map((f) => (
                        <SelectItem key={f} value={f}>
                          {labelOf(f)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </li>
              )}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
